import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Loader2, Tag, ChevronLeft, ChevronRight, Phone, Mail, ExternalLink } from 'lucide-react';
import ContactSellerButton from '@/components/ContactSellerButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CAT_LABELS = { vozila: 'Vozila', nekretnine: 'Nekretnine', elektronika: 'Elektronika', namjestaj: 'Namještaj', usluge: 'Usluge', sport: 'Sport / hobi', odjeca: 'Odjeća / obuća', poljoprivreda: 'Poljoprivreda', 'djecji-svijet': 'Dječji svijet', ostalo: 'Ostalo' };

function youtubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function ClassifiedDetailPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setItem(null); setError(null); setImgIdx(0);
    fetch(`${API}/public/classifieds/${id}`).then(async r => {
      if (!r.ok) { setError(r.status === 404 ? 'Oglas nije pronađen' : 'Greška pri učitavanju'); return; }
      const d = await r.json();
      setItem(d);
    }).catch(() => setError('Greška pri učitavanju'));
  }, [id]);

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-gray-500" data-testid="classified-error">{error} — <Link to="/oglasnik" className="text-red-600">natrag na Oglasnik</Link></div>;
  if (!item) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>;

  const images = item.images || [];
  const ytEmbed = youtubeEmbed(item.youtube_url);
  const resolveImg = (s) => (s?.startsWith('http') || s?.startsWith('data:')) ? s : `${BACKEND}${s}`;

  return (
    <>
      <Helmet><title>{`${item.title} — Oglasnik`}</title></Helmet>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="classified-detail">
        <Link to="/oglasnik" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 mb-4">
          <ChevronLeft size={14} /> Natrag na Oglasnik
        </Link>

        {/* Image gallery */}
        {images.length > 0 && (
          <div className="bg-gray-100 dark:bg-[#111827] rounded-xl overflow-hidden mb-6 relative" data-testid="classified-gallery">
            <img src={resolveImg(images[imgIdx])} alt="" className="w-full max-h-[500px] object-contain" />
            {images.length > 1 && (
              <>
                <button onClick={() => setImgIdx(i => (i - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full" aria-label="Prethodna" data-testid="gallery-prev"><ChevronLeft size={18} /></button>
                <button onClick={() => setImgIdx(i => (i + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full" aria-label="Sljedeća" data-testid="gallery-next"><ChevronRight size={18} /></button>
                <div className="absolute bottom-2 right-3 bg-black/60 text-white text-xs px-2 py-0.5 rounded">{imgIdx + 1} / {images.length}</div>
              </>
            )}
            {images.length > 1 && (
              <div className="flex gap-1.5 p-2 overflow-x-auto bg-gray-200 dark:bg-gray-800">
                {images.map((s, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className={`shrink-0 w-16 h-12 rounded overflow-hidden border-2 ${i === imgIdx ? 'border-red-500' : 'border-transparent'}`} data-testid={`thumb-${i}`}>
                    <img src={resolveImg(s)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <span className="inline-block text-[11px] font-bold uppercase text-white px-2 py-1 rounded bg-red-600 mb-3">
          {CAT_LABELS[item.category] || item.category}
        </span>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{item.title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
          {item.location && <span className="flex items-center gap-1"><MapPin size={14} />{item.location}</span>}
          {item.price_eur != null && (
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{item.price_eur.toLocaleString('hr')} €</span>
          )}
        </div>

        <div className="prose prose-sm dark:prose-invert max-w-none mb-6 whitespace-pre-wrap" data-testid="classified-description">
          {item.description}
        </div>

        {/* Video */}
        {item.video_url && (
          <video src={item.video_url} controls className="w-full max-h-[500px] rounded-xl mb-6 bg-black" data-testid="classified-video-player" />
        )}
        {ytEmbed && (
          <div className="aspect-video w-full mb-6">
            <iframe src={ytEmbed} title="YouTube" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full rounded-xl" data-testid="classified-youtube-iframe" />
          </div>
        )}

        {/* Contact */}
        <div className="bg-gray-50 dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-xl p-5 mt-6" data-testid="classified-contact">
          <h3 className="text-sm font-bold uppercase text-gray-500 dark:text-gray-400 mb-3">Kontakt</h3>
          <div className="space-y-1.5 text-sm">
            {item.contact_name && <p className="font-semibold text-gray-900 dark:text-gray-100">{item.contact_name}</p>}
            {item.contact_phone && (
              <p className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <Phone size={14} /> <a href={`tel:${item.contact_phone}`} className="hover:text-red-600">{item.contact_phone}</a>
              </p>
            )}
            {item.contact_email && (
              <p className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <Mail size={14} /> <a href={`mailto:${item.contact_email}`} className="hover:text-red-600">{item.contact_email}</a>
              </p>
            )}
            <ContactSellerButton listingType="classified" listingId={item.id} ownerName={item.contact_name} />
          </div>
        </div>
      </div>
    </>
  );
}
